import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    WithStyles
} from "@material-ui/core";
import { styles } from "./SubscriptionUserDateRangesDialog.styles";
import * as SubscriptionAction from "../../../stores/subscriptions/SubscriptionAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import {
    IUniversalTableAddAction,
    IUniversalTableColumn,
    IUniversalTableData
} from "../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { consolidateDateToNumber, convertDateStringToNumber, today } from "../../../utilities/dateTimeUtils";
import CloseIcon from "@material-ui/icons/Close";
import { DATE_FORMAT } from "../../../constants/TIME";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import SubscriptionUserModel from "../../../models/subscription/SubscriptionUserModel";

export interface ISubscriptionVisibilityDateDialogProps extends WithStyles<typeof styles> {
    subscriptionTypeId: number;
    subscriptionTitle: string;
    userId: string;
    userName: string;
    onClose: () => void;
}

const SubscriptionVisibilityDateDialogPure: React.FunctionComponent<ISubscriptionVisibilityDateDialogProps> = (
    props: ISubscriptionVisibilityDateDialogProps
) => {
    const { onClose, subscriptionTypeId, subscriptionTitle, userId, userName } = props;

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const tableRef = React.createRef();

    useEffect(() => {
        dispatch(SubscriptionAction.requestSubscriptionUsers(subscriptionTypeId));
    }, [subscriptionTypeId, dispatch]);

    const isRequesting = useSelector(state => selectRequesting(state, [SubscriptionAction.REQUEST_SUBSCRIPTION_USERS]));

    let visibilityData: IUniversalTableData;
    const subscriptionsFromState = useSelector(state => state.subscriptions);
    if (subscriptionsFromState?.subscriptionUsers) {
        const user: any = Object.values(subscriptionsFromState?.subscriptionUsers)?.find(
            (u: SubscriptionUserModel) => u.userId === userId
        );
        if (user) {
            if (user.visibilityStartDate || user.visibilityEndDate) {
                visibilityData = [
                    {
                        visibilityStartDate: user.visibilityStartDate,
                        visibilityEndDate: user.visibilityEndDate
                    }
                ];
            }
        }
    }

    const handleAddVisibilityDate = newVisibilityDates => {
        const sendVisibilityDates = {
            visibilityStartDate: consolidateDateToNumber(newVisibilityDates.visibilityStartDate),
            visibilityEndDate: consolidateDateToNumber(newVisibilityDates.visibilityEndDate)
        };

        if (sendVisibilityDates.visibilityStartDate === 0 && sendVisibilityDates.visibilityEndDate === 0) {
            return;
        }

        dispatch(SubscriptionAction.setUserVisibilityDate(subscriptionTypeId, userId, sendVisibilityDates))
            .then(() => {
                enqueueSnackbar("A new visibility date range has been added.", {
                    variant: ToastStatusEnum.Info
                });
                dispatch(SubscriptionAction.requestSubscriptionUsers(subscriptionTypeId));
            })
            .catch(e => {
                enqueueSnackbar("An error occured: " + e.message, {
                    variant: ToastStatusEnum.Info
                });
            });
    };

    const handleEditVisibilityDate = (index, newVisibilityDates) => {
        const sendVisibilityDates = {
            visibilityStartDate: consolidateDateToNumber(newVisibilityDates.visibilityStartDate),
            visibilityEndDate: consolidateDateToNumber(newVisibilityDates.visibilityEndDate)
        };

        if (sendVisibilityDates.visibilityStartDate === 0 && sendVisibilityDates.visibilityEndDate === 0) {
            handleDeleteVisibilityDate();
            return;
        }

        dispatch(SubscriptionAction.setUserVisibilityDate(subscriptionTypeId, userId, sendVisibilityDates)).then(() => {
            enqueueSnackbar("The visibility date range has been updated.", {
                variant: ToastStatusEnum.Info
            });
            dispatch(SubscriptionAction.requestSubscriptionUsers(subscriptionTypeId));
        })
        .catch(e => {
            enqueueSnackbar("An error occured: " + e.message, {
                variant: ToastStatusEnum.Info
            });
        });
    };

    const handleDeleteVisibilityDate = () => {
        dispatch(SubscriptionAction.deleteUserVisibilityDate(subscriptionTypeId, userId)).then(() => {
            enqueueSnackbar("The visibility date range has been deleted.", {
                variant: ToastStatusEnum.Info
            });
            dispatch(SubscriptionAction.requestSubscriptionUsers(subscriptionTypeId));
        });
    };

    const handleClose = () => {
        onClose();
    };

    const visibilityTableColumns: IUniversalTableColumn[] = [
        {
            type: "date_with_option",
            title: "Visibility start date",
            field: "visibilityStartDate",
            defaultValue: convertDateStringToNumber(today().format(DATE_FORMAT)),
            label: "Not set",
            firstAvailableDate: convertDateStringToNumber(today().format(DATE_FORMAT)),
            validate: rowData => {
                return rowData?.visibilityStartDate === 0 || rowData?.visibilityEndDate === 0
                    ? true
                    : rowData?.visibilityStartDate > rowData?.visibilityEndDate
                    ? { isValid: false, helperText: t("admin.manageSubscriptionUserDateRangesView.startDateError") }
                    : true;
            }
        },
        {
            type: "date_with_option",
            title: "Visibility end date",
            field: "visibilityEndDate",
            defaultValue: convertDateStringToNumber(today().add(1, "M").format(DATE_FORMAT)),
            label: "Not set",
            firstAvailableDate: convertDateStringToNumber(today().format(DATE_FORMAT)),
            validate: rowData => {
                return rowData?.visibilityStartDate === 0 || rowData?.visibilityEndDate === 0
                    ? true
                    : rowData?.visibilityStartDate > rowData?.visibilityEndDate
                    ? { isValid: false, helperText: t("admin.manageSubscriptionUserDateRangesView.startDateError") }
                    : true;
            }
        },
        {
            type: "actions",
            title: "Actions",
            actions: [
                {
                    name: "add",
                    tooltip: "Add visibility date.",
                    disabled: visibilityData?.length > 0,
                    onAddFinished: newVisibilityDates => handleAddVisibilityDate(newVisibilityDates)
                } as IUniversalTableAddAction,
                {
                    name: "edit",
                    tooltip: "Edit visibility date.",
                    onEditFinished: (index, newValue) => handleEditVisibilityDate(index, newValue)
                },
                {
                    name: "delete",
                    tooltip: "Delete visibility date.",
                    onDeleteFinished: () => handleDeleteVisibilityDate(),
                    deleteQuestion: "Are you sure you want to delete the visibility range?"
                }
            ]
        }
    ];

    return (
        <>
            <Dialog open fullWidth scroll="paper" onClose={handleClose} maxWidth="md">
                <DialogTitle>
                    <Typography variant="subtitle1">Edit Visibility Date</Typography>
                    <br />
                    {t("general.subscription")}: {subscriptionTitle}
                    <br />
                    {t("general.user")}: {userName}
                    <IconButton
                        aria-label="close"
                        style={{ position: "absolute", right: "1px", top: "1px" }}
                        onClick={handleClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <UniversalTable
                        tableRef={tableRef}
                        columns={visibilityTableColumns}
                        data={visibilityData}
                        emptyDataMessage={"No visibility date."}
                        pagingDisabled={true}
                        isLoading={isRequesting}
                    />
                </DialogContent>
                <DialogActions>
                    <BlueButton onClick={handleClose}>{t("general.close")}</BlueButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const SubscriptionVisibilityDateDialog = withStyles(styles)(SubscriptionVisibilityDateDialogPure);
